import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, MapPin, Users, ChefHat, Star, Calendar, Clock, Loader, AlertCircle, DollarSign, Utensils, MessageCircle, User, Beer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useError } from '../contexts/ErrorContext';
import LocationSearch from '../components/LocationSearch';
import LoadingIndicator from '../components/LoadingIndicator';
import FilterSection from '../components/FilterSection';
import { cuisineOptions, priceRangeOptions, dietaryOptions } from '../data/filterOptions';
import BackButton from '../components/BackButton';
import { getNearbyListings, Listing } from '../lib/listings';
import { createChatRoom, sendMessage } from '../lib/chat';

const NearbyListings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { setError } = useError();
  const [loading, setLoading] = useState(true);
  const [error, setLocalError] = useState<string | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
  const [creatingChat, setCreatingChat] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [animateCards, setAnimateCards] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRadius, setSearchRadius] = useState(50);
  const [selectedLocation, setSelectedLocation] = useState<{
    address: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchNearbyListings();
    }
  }, [selectedLocation, searchRadius]);

  useEffect(() => {
    // Apply filtering whenever search query or filters change
    let filtered = listings;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(listing => 
        (listing.venue_name && listing.venue_name.toLowerCase().includes(query)) ||
        (listing.user_name && listing.user_name.toLowerCase().includes(query)) ||
        (listing.venue_cuisine_type && listing.venue_cuisine_type.toLowerCase().includes(query)) ||
        (listing.venue_bar_type && listing.venue_bar_type.toLowerCase().includes(query)) ||
        (listing.preferences && listing.preferences.toLowerCase().includes(query)) ||
        (listing.description && listing.description.toLowerCase().includes(query))
      );
    }
    
    if (selectedFilters.length > 0) {
      filtered = filtered.filter(listing => {
        return selectedFilters.some(filter => {
          // Check price range
          if (filter === listing.venue_price_range) {
            return true;
          }
          
          // Check cuisine type
          if (listing.venue_cuisine_type && listing.venue_cuisine_type.toLowerCase() === filter.toLowerCase()) {
            return true;
          }
          
          // Check bar type
          if (listing.venue_bar_type && listing.venue_bar_type.toLowerCase() === filter.toLowerCase()) {
            return true;
          }
          
          return false;
        });
      });
    }
    
    setFilteredListings(filtered);
  }, [listings, searchQuery, selectedFilters]);

  useEffect(() => {
    // Trigger card animations after initial load
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [filteredListings]);

  const initialize = async () => {
    try {
      setLoading(true);
      setLocalError(null);

      // Check authentication status
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        console.error("Auth error:", authError);
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(!!session);
        setCurrentUser(session?.user || null);
      }

      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('location_lat, location_lng, location')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          console.warn("Error fetching profile:", profileError);
        } else if (profile?.location_lat && profile?.location_lng) {
          setSelectedLocation({
            address: profile.location || '',
            lat: profile.location_lat,
            lng: profile.location_lng
          });
          setSearchQuery('');
        }
      }
    } catch (err) {
      console.error('Error initializing:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyListings = async () => {
    if (!selectedLocation) return;
    
    try {
      setLoading(true);
      setLocalError(null);

      const nearbyListings = await getNearbyListings(
        selectedLocation.lat,
        selectedLocation.lng,
        searchRadius
      );

      // Filter out the current user's listings
      const filteredListings = currentUser 
        ? nearbyListings.filter(listing => listing.user_id !== currentUser.id)
        : nearbyListings;

      setListings(filteredListings);
      setFilteredListings(filteredListings);
    } catch (err) {
      console.error('Error fetching nearby listings:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to fetch nearby listings');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setSelectedLocation(location);
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const handleMessageUser = async (e: React.MouseEvent, userId: string, listingId: string) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      setCreatingChat(listingId);
      
      // Create or get chat room
      const chatRoomId = await createChatRoom([userId]);
      
      // Navigate to the chat
      navigate(`/chat/${chatRoomId}`);
    } catch (err) {
      console.error('Error creating chat:', err);
      setError(new Error('Failed to create chat'), 'CHAT_ERROR');
    } finally {
      setCreatingChat(null);
    }
  };

  const navigateToUserProfile = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  if (loading && isAuthenticated === null) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                Dinner Wünsche in deiner Nähe
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                Finde Leute für ein gemeinsames Abendessen in deiner Nähe
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <LocationSearch
              onLocationSelect={handleLocationSelect}
              defaultValue={selectedLocation?.address || ''}
            />
          </div>
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Suche Anfragen..."
              className="pl-10 w-full p-3 border-2 border-gray-200 rounded-xl transition-all duration-300 focus:border-orange-500 focus:outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Filter className="h-5 w-5" />
            <span>{showFilters ? t('search.hideFilters') : t('search.showFilters')}</span>
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Suchradius: {searchRadius} km
          </label>
          <input
            type="range"
            min="5"
            max="100"
            value={searchRadius}
            onChange={(e) => setSearchRadius(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>5 Kilometer</span>
            <span>100 Kilometer</span>
          </div>
        </div>

        {showFilters && (
          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t animate-slideDown">
            <FilterSection
              title={t('search.cuisine')}
              icon={ChefHat}
              options={cuisineOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('search.priceRange')}
              icon={DollarSign}
              options={priceRangeOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('common.dietaryOptions')}
              icon={Utensils}
              options={dietaryOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        )}
      </div>

      {/* Results Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredListings.map((listing, index) => (
          <div
            key={listing.id}
            className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
              animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {/* User Profile Section - Much Larger Profile Picture */}
            <div 
              className="cursor-pointer flex flex-col items-center justify-center p-6 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-orange-100"
              onClick={() => navigateToUserProfile(listing.user_id)}
            >
              {listing.user_profile_picture ? (
                <img
                  src={listing.user_profile_picture}
                  alt={listing.user_name || 'User'}
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md hover:scale-105 transition-transform"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-orange-200 flex items-center justify-center border-4 border-white shadow-md">
                  <User className="h-16 w-16 text-orange-500" />
                </div>
              )}
              <h3 className="mt-3 text-lg font-semibold text-gray-900 hover:text-orange-500 transition-colors">
                {listing.user_name || 'Anonymous User'}
              </h3>
              <div className="flex items-center text-xs text-gray-500 mt-1">
                <Calendar className="h-3 w-3 mr-1" />
                <span>{new Date(listing.date).toLocaleDateString()}</span>
                {listing.meeting_time && (
                  <>
                    <span className="mx-1">•</span>
                    <Clock className="h-3 w-3 mr-1" />
                    <span>{listing.meeting_time.slice(0, 5)}</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="p-4">
              <div className="mb-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-base font-medium text-gray-900">
                    {listing.venue_name}
                  </h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    listing.venue_type === 'restaurant' 
                      ? 'bg-orange-100 text-orange-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {listing.venue_type === 'restaurant' ? 'Restaurant' : 'Bar'}
                  </span>
                </div>
                <div className="text-gray-600 text-sm">
                  {listing.venue_type === 'restaurant' ? (
                    <>
                      <ChefHat className="h-3 w-3 inline mr-1" />
                      <span>{listing.venue_cuisine_type}</span>
                    </>
                  ) : (
                    <>
                      <Beer className="h-3 w-3 inline mr-1" />
                      <span>{listing.venue_bar_type}</span>
                    </>
                  )}
                  {listing.venue_price_range && (
                    <>
                      <span className="mx-1">•</span>
                      <span>{listing.venue_price_range}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-1 mb-3">
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Users className="h-3 w-3" />
                  <span>Suche {listing.party_size - 1}  {listing.party_size - 1 === 1 ? 'Person' : 'Personen'}</span>
                </div>
                
                {listing.distance !== undefined && (
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <MapPin className="h-3 w-3" />
                    <span>{listing.distance.toFixed(1)} KM entfernt</span>
                  </div>
                )}
              </div>

              {listing.preferences && (
                <div className="mb-2">
                  <p className="text-xs text-gray-600">{listing.preferences}</p>
                </div>
              )}

              {listing.description && (
                <div className="mb-3">
                  <p className="text-xs text-gray-600 line-clamp-2">{listing.description}</p>
                </div>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessageUser(e, listing.user_id, listing.id);
                }}
                disabled={creatingChat === listing.id}
                className="w-full bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center justify-center space-x-2 disabled:opacity-75 text-sm"
              >
                {creatingChat === listing.id ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Chat öffnen...</span>
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-4 w-4" />
                    <span>Schreibe {listing.user_name?.split(' ')[0]}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredListings.length === 0 && selectedLocation && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center transform transition-all duration-500 hover:shadow-xl">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">Leider nichts gefunden...</h3>
          <p className="text-gray-600 mb-8">
            Es gibt keine Dinneranfragen in deine Umgebung. Sei der erste der eine erstellt!
          </p>
          <Link
            to="/discover"
            className="inline-flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Users className="h-5 w-5" />
            <span>Anfrage erstellen</span>
          </Link>
        </div>
      )}

      {!selectedLocation && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center transform transition-all duration-500 hover:shadow-xl">
          <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">Wähle eine Location</h3>
          <p className="text-gray-600">
            Bitte gib eine Location ein um Anfragen in dessen Umgebung zu sehen.
          </p>
        </div>
      )}
    </div>
  );
};

export default NearbyListings;